import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';
import { Modal, ModalBody, ModalHeader, ModalFooter, Row, Col, Table, Button, Alert } from 'reactstrap';
import { FormattedDate, FormattedTime } from 'react-intl';
import { SecureContent } from 'core/components';
import * as roles from 'core/model/roles';

import { toggleModal } from 'core/ducks/ui/modal';
import { WktGeometryMap } from 'input-fields';
import T from 'modules/i18n';

class ViewLicence extends Component {

  constructor(props) {
    super(props);
    this.state = {
      values: this.props.data,
      stationProfile: null,
      stationProfileLoading: false,
      stationProfileError: null,
      stationUpdateLoading: false,
      stationUpdateError: null,
      stationUpdateSuccess: null,
    };

    this.handleStationUpdate = this.handleStationUpdate.bind(this);
  }

  componentDidMount() {
    this.loadStationProfile();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.setState(
        {
          values: this.props.data,
          stationUpdateError: null,
          stationUpdateSuccess: null,
        },
        () => this.loadStationProfile()
      );
    }
  }

  hasStationId(values) {
    if (!values || values.station_id === null || values.station_id === undefined) {
      return false;
    }
    return String(values.station_id).trim() !== '';
  }

  handleStationUpdate() {
    const { values, stationProfile } = this.state;

    if (!this.hasStationId(values)) {
      this.setState({ stationUpdateError: 'Δεν υπάρχει κωδικός σταθμού στην άδεια για ενημέρωση.' });
      return;
    }

    if (!this.props.updateStation) {
      return;
    }

    this.setState({
      stationUpdateLoading: true,
      stationUpdateError: null,
      stationUpdateSuccess: null,
    });

    this.props.updateStation(values, stationProfile)
      .then(() => {
        this.setState({
          stationUpdateLoading: false,
          stationUpdateSuccess: 'Η ενημέρωση σταθμού ολοκληρώθηκε επιτυχώς.',
        });
      })
      .catch((error) => {
        this.setState({
          stationUpdateLoading: false,
          stationUpdateError: (error && error.message)
            ? error.message
            : 'Η ενημέρωση σταθμού απέτυχε.',
        });
      });
  }

  getStationFromProfile(stationProfile) {
    const raw = stationProfile && stationProfile.stationData;
    if (!raw) {
      return null;
    }
    if (Array.isArray(raw)) {
      return raw.length > 0 ? raw[0] : null;
    }
    if (Array.isArray(raw.value)) {
      return raw.value.length > 0 ? raw.value[0] : null;
    }
    if (raw.stationId || raw.stationid) {
      return raw;
    }
    return null;
  }

  loadStationProfile() {
    const { values } = this.state;
    const stationId = values && values.station_id;
    const filter = values && values.licence_number;

    if (!stationId || !filter) {
      return;
    }

    this.setState({ stationProfileLoading: true, stationProfileError: null });

    this.props.fetchStationProfile(stationId, filter)
      .then((data) => {
        this.setState({
          stationProfile: data && typeof data === 'object' ? data : null,
          stationProfileLoading: false,
        });
      })
      .catch((error) => {
        this.setState({
          stationProfileLoading: false,
          stationProfileError: error.message || 'Failed to load station profile',
        });
      });
  }

  renderStationDataTable(station) {
    const rows = [
      ['Κωδικός σταθμού', station.stationId],
      ['Τίτλος', station.title],
      ['Περιγραφή', station.description],
      ['Τεχνολογία', station.technologyName],
      ['Οργανισμός', station.organizationName],
      ['Πελάτης', station.clientFullName],
      ['ΑΦΜ ιδιοκτήτη', station.ownerVatNumber],
      ['Κατάσταση', station.status],
      ['Εγκατεστημένη ισχύς (MW)', station.installedPower],
      ['Μέγιστη ισχύς (MW)', station.maxPower],
      ['Περιφέρεια', station.region],
      ['Περιφερειακή ενότητα', station.regionSection],
      ['Δήμος', station.municipality],
      ['Δημοτική ενότητα', station.municipalitySection],
      ['Ενεργειακή κατηγορία', station.energyCategory],
      ['Περιβαλλοντική κατηγορία', station.environmentalCategory],
      ['Υποσταθμός', station.substation],
      ['Ημερομηνία ενημέρωσης', station.updateDate],
      ['Fast track', station.isFastTrack ? 'ΝΑΙ' : 'ΌΧΙ'],
      ['Εξαιρούμενος', station.isExcluded ? 'ΝΑΙ' : 'ΌΧΙ'],
      ['Με μπαταρία', station.withBattery ? 'ΝΑΙ' : 'ΌΧΙ'],
      ['Κορεσμένο δίκτυο', station.saturatedNetwork ? 'ΝΑΙ' : 'ΌΧΙ'],
      ['Κατάσταση αδειοδότησης', station.licensingStatus],
    ];

    return (
      <Table striped bordered>
        <tbody>
          {rows.map(([label, value]) => (
            value !== null && value !== undefined && String(value).trim() !== '' && (
              <tr key={label}>
                <th>{label}</th>
                <td><span>{value}</span></td>
              </tr>
            )
          ))}
        </tbody>
      </Table>
    );
  }

  parseFeatureProperties(feature) {
    if (!feature || !feature.propertiesJson) {
      return {};
    }
    try {
      return JSON.parse(feature.propertiesJson);
    } catch (e) {
      return {};
    }
  }

  formatProducerName(personalInfo) {
    if (!personalInfo) {
      return '';
    }
    if (personalInfo.isLegalEntity) {
      return personalInfo.name || '';
    }
    return [personalInfo.firstName, personalInfo.name].filter(Boolean).join(' ');
  }

  renderProducerPersonSection(title, person) {
    if (!person) {
      return null;
    }

    const { personalInfo, addresses, phones, files } = person;
    const info = personalInfo || {};
    const primaryAddress = (addresses || []).find((a) => a.isPrimary) || (addresses || [])[0];
    const primaryPhone = (phones || []).find((p) => p.isPrimary) || (phones || [])[0];
    const displayName = this.formatProducerName(info);

    const rows = [
      ['Ονομασία', displayName],
      ['ΑΦΜ', info.vatNumber],
      ['Νομικό πρόσωπο', info.isLegalEntity ? 'ΝΑΙ' : 'ΌΧΙ'],
      ['Email', info.email],
      ['ΑΔΤ', info.idCardNumber],
    ];

    if (primaryAddress) {
      const addressLine = [
        primaryAddress.address,
        primaryAddress.addressNumber,
        primaryAddress.tk,
        primaryAddress.otaDivisionName,
      ].filter((v) => v !== null && v !== undefined && String(v).trim() !== '').join(', ');
      rows.push(['Διεύθυνση', addressLine]);
    }

    if (primaryPhone) {
      rows.push(['Τηλέφωνο', primaryPhone.phone]);
    }

    return (
      <div className="mb-3">
        <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{title}</p>
        <Table striped bordered className="mb-2">
          <tbody>
            {rows.map(([label, value]) => (
              value !== null && value !== undefined && String(value).trim() !== '' && (
                <tr key={`${title}-${label}`}>
                  <th>{label}</th>
                  <td><span>{value}</span></td>
                </tr>
              )
            ))}
          </tbody>
        </Table>
        {files && files.length > 0 && (
          <Table striped bordered size="sm">
            <thead>
              <tr>
                <th>Έγγραφο</th>
                <th>Αρχείο</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file, index) => (
                <tr key={`${title}-file-${index}`}>
                  <td><span>{file.documentTypeName}</span></td>
                  <td><span>{file.fileName}</span></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    );
  }

  renderProducerInfo(producerInfo) {
    if (!producerInfo || typeof producerInfo !== 'object') {
      return null;
    }

    const { owner, administrator, representatives } = producerInfo;

    return (
      <>
        {this.renderProducerPersonSection('Ιδιοκτήτης', owner)}
        {this.renderProducerPersonSection('Διαχειριστής', administrator)}
        {Array.isArray(representatives) && representatives.map((rep, index) => (
          this.renderProducerPersonSection(`Αντίκλητος ${index + 1}`, rep)
        ))}
      </>
    );
  }

  renderMilestonesTable(milestones) {
    return (
      <Table striped bordered>
        <thead>
          <tr>
            <th>Ορόσημο</th>
            <th>Περιγραφή</th>
            <th>Υπόλοιπο ημέρες</th>
            <th>Μήνυμα</th>
          </tr>
        </thead>
        <tbody>
          {milestones.map((item, index) => (
            <tr key={index}>
              <td><span>{item.milestoneName}</span></td>
              <td><span style={{ whiteSpace: 'pre-wrap' }}>{item.milestoneDescription}</span></td>
              <td><span>{item.remainingDays}</span></td>
              <td><span>{item.message}</span></td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderFeatureTable(feature) {
    const properties = this.parseFeatureProperties(feature);

    const rows = [
      // ['Κωδικός ΡΑΕ', feature.raeCode],
      // ['Πηγή', feature.sourceType],
      ['Επίπεδο', feature.layerTitle],
      ['Τεχνολογία', feature.technologyName],
      // ['Αναγνωριστικό', feature.featureIdentifier],
      // ['Α/Α', properties.aa],
      // ['Εταιρεία', properties.company],
      // ['Ισχύς (MW)', properties.power_mw ?? properties.max_power],
      // ['Θέση', properties.thesh],
      // ['Κατάσταση', properties.katastash_descr ?? properties.katastasi],
      // ['Περιφέρεια', properties.kal_perifereia],
      // ['Δήμος', properties.kal_dhmos],
      // ['Δημοτική ενότητα', properties.kal_dhm_enothta],
      // ['Ημερομηνία', properties.imerominia],
      // ['Ημ. έκδοσης άδειας', properties.imerominia_ekdosis_adeias],
      ['Ημερομηνία πηγής', feature.sourceTimestamp],
      ['Ημ. εισαγωγής', feature.importedAt],
    ];

    return (
      <Table striped bordered>
        <tbody>
          {rows.map(([label, value]) => (
            value !== null && value !== undefined && String(value).trim() !== '' && (
              <tr key={label}>
                <th>{label}</th>
                <td><span>{value}</span></td>
              </tr>
            )
          ))}
        </tbody>
      </Table>
    );
  }

  render() {

    const {
      values,
      stationProfile,
      stationProfileLoading,
      stationProfileError,
      stationUpdateLoading,
      stationUpdateError,
      stationUpdateSuccess,
    } = this.state;
    const station = this.getStationFromProfile(stationProfile);
    const milestonesList = stationProfile && stationProfile.milestones;
    const milestones = Array.isArray(milestonesList) ? milestonesList : [];
    const producerInfo = stationProfile && stationProfile.producerInfo;
    const hasProducerInfo = producerInfo && typeof producerInfo === 'object' && (
      producerInfo.owner || producerInfo.administrator
      || (Array.isArray(producerInfo.representatives) && producerInfo.representatives.length > 0)
    );
    const featureList = stationProfile && stationProfile.feature;
    const feature = Array.isArray(featureList) && featureList.length > 0 ? featureList[0] : null;

    return (
      <Modal isOpen={this.props.isOpen} className="modal-xl">
        <ModalHeader toggle={() => this.props.dispatch(toggleModal())}>
        <T>station_id</T>:<span className="font-weight-bold" >{' ' + values.station_id}</span> - <T>licence_number</T>:<span className="font-weight-bold" >{' ' + values.licence_number}</span>
        </ModalHeader>
        <ModalBody>
          <Table striped bordered>
            <tbody>
              <tr><th><T>kind</T></th><td><span>{values.kind}</span></td></tr>
              <tr><th><T>technology_ape</T></th><td><span>{values.technology_ape}</span></td></tr>
              <tr><th><T>technology_storage</T></th><td><span>{values.technology_storage}</span></td></tr>
              <tr><th><T>issue_date</T></th><td><FormattedDate value={values.issue_date} year='numeric' month='long' day='numeric' /></td></tr>
              <tr><th><T>expiration_date</T></th><td>{values.expiration_date && <FormattedDate value={values.expiration_date} year='numeric' month='long' day='numeric' />}</td></tr>
              <tr><th><T>renewal_expiration_date</T></th><td>{values.renewal_expiration_date && <FormattedDate value={values.renewal_expiration_date} year='numeric' month='long' day='numeric' />}</td></tr>
              <tr><th><T>external_url_link</T> (αρχική)</th><td><a href={values.external_url_link} target="_blank" rel="noopener noreferrer">
                {values.external_url_link}
              </a></td></tr>
              <tr><th><T>licence_status</T></th><td><span>{values.licence_status}</span></td></tr>
              <tr><th><T>is_special</T></th><td><span>{values.is_special ? 'ΝΑΙ' : 'ΌΧΙ'}</span></td></tr>
              <tr><th><T>cert_number</T></th><td><span>{values.cert_number}</span></td></tr>
              <tr><th><T>primary_registry</T> (αρχική)</th><td><span>{values.primary_registry}</span></td></tr>
              <tr><th><T>mod_decisions</T></th><td><span>{values.mod_decisions && JSON.parse(values.mod_decisions).join(', ')}</span></td></tr>
              <tr><th><T>progress_reports</T></th><td><span>{values.mod_decisions && JSON.parse(values.mod_decisions).join(', ')}</span></td></tr>
            </tbody>
          </Table>
          <hr />
          <p style={{color: '#3399ff', fontWeight: 600}}>Τοποθεσία</p>
          <Table striped bordered>
            <tbody>
              <tr><th><T>region</T></th><td><span>{values.region}</span></td></tr>
              <tr><th><T>regional_section</T></th><td><span>{values.regional_section}</span></td></tr>
              <tr><th><T>municipality</T></th><td><span>{values.municipality}</span></td></tr>
              <tr><th><T>municipal_section</T></th><td><span>{values.municipal_section}</span></td></tr>
              <tr><th><T>position</T></th><td><span>{values.position}</span></td></tr>
            </tbody>
          </Table>
          <hr />
          <p style={{color: '#3399ff', fontWeight: 600}}>Ισχύς - Χωρητικότητα</p>
          <Table striped bordered>
            <tbody>
              <tr><th><T>inst_mw</T></th><td><span>{values.inst_mw}</span></td></tr>
              <tr><th><T>mw</T></th><td><span>{values.mw}</span></td></tr>
              <tr><th><T>egk_capac</T></th><td><span>{values.egk_capac}</span></td></tr>
              <tr><th><T>guar_capac</T></th><td><span>{values.guar_capac}</span></td></tr>
              <tr><th><T>guar_power</T></th><td><span>{values.guar_power}</span></td></tr>
              <tr><th><T>max_pwr_ap</T></th><td><span>{values.max_pwr_ap}</span></td></tr>
              <tr><th><T>max_pwr_eg</T></th><td><span>{values.max_pwr_eg}</span></td></tr>
              <tr><th><T>max_production_capacity_kwhth</T></th><td><span>{values.max_production_capacity_kwhth}</span></td></tr>
            </tbody>
          </Table>
          <hr />
          <p style={{color: '#3399ff', fontWeight: 600}}>Φορέας - Αντίκλητος</p>
          <Table striped bordered>
            <tbody>
              <tr><th><T>org_identifier</T></th><td><span>{values.org_identifier}</span></td><th><T>proxy_identifier</T></th><td><span>{values.proxy_identifier}</span></td></tr>
              <tr><th><T>org_name</T></th><td><span>{values.org_name}</span></td><th><T>proxy_fullname</T></th><td><span>{values.proxy_fullname}</span></td></tr>
              <tr><th><T>org_name_mitroo</T></th><td><span>{values.org_name_mitroo}</span></td><th><T>proxy_email</T></th><td><span>{values.proxy_email}</span></td></tr>
              <tr><th><T>org_title</T></th><td><span>{values.org_title}</span></td><th><T>proxy_phonenumber</T></th><td><span>{values.proxy_phonenumber}</span></td></tr>
              <tr><th><T>org_address</T></th><td><span>{values.org_address}</span></td><th></th><td><span></span></td></tr>
            </tbody>
          </Table>
          <hr />
          <SecureContent role={roles.REVIEWER}>
            <p style={{color: '#3399ff', fontWeight: 600}}>Προφίλ Σταθμού (ΠΣΑΠΕ)</p>
            {stationProfileLoading && (
              <p>Φόρτωση προφίλ σταθμού...</p>
            )}
            {stationProfileError && !stationProfileLoading && (
              <p style={{ color: 'red' }}>Σφάλμα φόρτωσης προφίλ σταθμού: {stationProfileError}</p>
            )}
            {!stationProfileLoading && !stationProfileError && station && (
              this.renderStationDataTable(station)
            )}
            {!stationProfileLoading && !stationProfileError && feature && (
              <>
                <hr />
                <p style={{ color: '#3399ff', fontWeight: 600 }}>Χαρακτηριστικό άδειας (ΠΣΑΠΕ)</p>
                {this.renderFeatureTable(feature)}
                {feature.geometry && (
                  <>
                    {/* <p style={{ color: '#3399ff', fontWeight: 600, marginTop: '1rem' }}>Χάρτης</p> */}
                    <p className="text-muted small mb-2">Κάντε κλικ στο πολύγωνο για να δείτε τις ιδιότητες.</p>
                    <WktGeometryMap
                      wkt={feature.geometry}
                      properties={this.parseFeatureProperties(feature)}
                      height={360}
                    />
                  </>
                )}
              </>
            )}
            {!stationProfileLoading && !stationProfileError && milestones.length > 0 && (
              <>
                <hr />
                <p style={{ color: '#3399ff', fontWeight: 600 }}>Ορόσημα Σταθμού (ΠΣΑΠΕ)</p>
                {this.renderMilestonesTable(milestones)}
              </>
            )}
            {!stationProfileLoading && !stationProfileError && hasProducerInfo && (
              <>
                <hr />
                <p style={{ color: '#3399ff', fontWeight: 600 }}>Στοιχεία Παραγωγού (ΠΣΑΠΕ)</p>
                {this.renderProducerInfo(producerInfo)}
              </>
            )}
            {!stationProfileLoading && !stationProfileError && stationProfile && !station && !feature && milestones.length === 0 && !hasProducerInfo && (
              <p>Δεν βρέθηκαν δεδομένα σταθμού στο ΠΣΑΠΕ.</p>
            )}
          </SecureContent>
        </ModalBody>
        <SecureContent role={roles.EDITOR}>
          <ModalFooter className="flex-column align-items-stretch">
            {stationUpdateError && (
              <Alert color="danger" className="mb-2 w-100">
                {stationUpdateError}
              </Alert>
            )}
            {stationUpdateSuccess && (
              <Alert color="success" className="mb-2 w-100">
                {stationUpdateSuccess}
              </Alert>
            )}
            <Button
              color="primary"
              className="w-100"
              onClick={this.handleStationUpdate}
              disabled={stationUpdateLoading || !this.hasStationId(values)}
            >
              {stationUpdateLoading ? 'Ενημέρωση σε εξέλιξη...' : 'Ενημέρωση Σταθμού'}
            </Button>
          </ModalFooter>
        </SecureContent>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  isOpen: state.ui.modal.modalOpen,
  list: state.list,
});

ViewLicence.propTypes = {
  data: PropTypes.object,
  uuid: PropTypes.string,
  fetchStationProfile: PropTypes.func,
  updateStation: PropTypes.func,
};

ViewLicence = connect(mapStateToProps)(ViewLicence);

export default ViewLicence;
